/** This file must only contain pure code and pure imports */
/* eslint-disable @typescript-eslint/naming-convention */
import { RegisterInstancedMesh } from "core/Meshes/instancedMesh.pure";
import { RegisterThinInstanceMesh } from "core/Meshes/thinInstanceMesh.pure";
import { RegisterPbrMaterial } from "core/Materials/PBR/pbrMaterial.pure";
import { Tools } from "core/Misc/tools.pure";
import { AssetContainer } from "core/assetContainer";
import { RegisterSceneLoaderPlugin, } from "core/Loading/sceneLoader";
import { USDFileLoaderMetadata } from "./usdFileLoader.metadata";
import { materializeCommandBuffers } from "./usdSceneMaterializer";
import { PROTOCOL_VERSION } from "./usdCommandProtocol";
const _DefaultAssetRootUrl = `${Tools._DefaultCdnUrl}/babylonUsdImporter/${PROTOCOL_VERSION}/`;
const _SupportedExtension = /\.(?:usd|usda|usdc|usdz)$/i;
function _toBytes(input) {
    if (input instanceof Uint8Array) {
        return input.slice();
    }
    if (input instanceof ArrayBuffer) {
        return new Uint8Array(input.slice(0));
    }
    return new Uint8Array(input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength));
}
function _normalizeFiles(files) {
    if (!files) {
        return undefined;
    }
    const normalized = {};
    for (const [path, input] of Object.entries(files)) {
        normalized[path] = _toBytes(input);
    }
    return normalized;
}
function _stagedFileName(fileName, bytes, preserveUrlCharacters) {
    const normalizedFileName = fileName?.replace(/\\/g, "/").replace(/^\.\/+/, "");
    const cleanFileName = preserveUrlCharacters ? normalizedFileName : normalizedFileName?.split(/[?#]/, 1)[0];
    const pathParts = cleanFileName?.split("/").filter(Boolean);
    if (cleanFileName && !cleanFileName.startsWith("/") && pathParts?.length && !pathParts.includes("..") && _SupportedExtension.test(cleanFileName)) {
        return pathParts.join("/");
    }
    const isZip = bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
    return isZip ? "scene.usdz" : "scene.usd";
}
function _toAsyncResult(container) {
    return {
        meshes: container.meshes,
        particleSystems: container.particleSystems,
        skeletons: container.skeletons,
        animationGroups: container.animationGroups,
        transformNodes: container.transformNodes,
        geometries: container.geometries,
        lights: container.lights,
        spriteManagers: container.spriteManagers,
    };
}
/**
 * OpenUSD scene loader backed by a WebAssembly command-buffer extractor.
 */
export class USDFileLoader {
    /**
     * Creates a USD loader.
     * @param options Options controlling worker assets, supporting files, and diagnostics.
     */
    constructor(options = {}) {
        /**
         * Defines the name of the plugin.
         */
        this.name = USDFileLoaderMetadata.name;
        /**
         * Defines the extensions the plugin can load.
         */
        this.extensions = USDFileLoaderMetadata.extensions;
        this._nextRequestId = 1;
        this._pending = new Map();
        this._activeLoads = new Set();
        this._options = {
            ...options,
            resolveByFileName: options.resolveByFileName ?? true,
            glueUrl: options.glueUrl ?? USDFileLoader.DefaultConfiguration.glueUrl,
            wasmUrl: options.wasmUrl ?? USDFileLoader.DefaultConfiguration.wasmUrl,
            dataUrl: options.dataUrl ?? USDFileLoader.DefaultConfiguration.dataUrl,
            workerUrl: options.workerUrl ?? USDFileLoader.DefaultConfiguration.workerUrl,
        };
    }
    /**
     * Creates a configured plugin instance for a SceneLoader operation.
     * @param options SceneLoader plugin options.
     * @returns The configured USD loader.
     */
    createPlugin(options) {
        _RegisterUSDLoaderDependencies();
        return new USDFileLoader(options[USDFileLoaderMetadata.name]);
    }
    /**
     * Imports all objects from a USD stage into a scene.
     * @param _meshesNames Mesh name filtering is not currently supported.
     * @param scene The scene receiving the imported objects.
     * @param data The USD, USDA, USDC, or USDZ bytes.
     * @param rootUrl The source root URL.
     * @param onProgress SceneLoader progress callback.
     * @param fileName Name of the root USD layer.
     * @returns The imported Babylon.js objects.
     */
    async importMeshAsync(_meshesNames, scene, data, rootUrl, onProgress, fileName) {
        const result = await this._loadAsync(scene, data, rootUrl, onProgress, fileName, true);
        return _toAsyncResult(result.container);
    }
    /**
     * Loads a USD stage into a scene.
     * @param scene The scene receiving the imported objects.
     * @param data The USD, USDA, USDC, or USDZ bytes.
     * @param rootUrl The source root URL.
     * @param onProgress SceneLoader progress callback.
     * @param fileName Name of the root USD layer.
     */
    async loadAsync(scene, data, rootUrl, onProgress, fileName) {
        await this._loadAsync(scene, data, rootUrl, onProgress, fileName, true);
    }
    /**
     * Loads a USD stage into an asset container.
     * @param scene The scene used to create imported objects.
     * @param data The USD, USDA, USDC, or USDZ bytes.
     * @param rootUrl The source root URL.
     * @param onProgress SceneLoader progress callback.
     * @param fileName Name of the root USD layer.
     * @returns The populated asset container.
     */
    async loadAssetContainerAsync(scene, data, rootUrl, onProgress, fileName) {
        const container = (await this._loadAsync(scene, data, rootUrl, onProgress, fileName, false)).container;
        if (!(container instanceof AssetContainer)) {
            throw new Error("USDFileLoader failed to create an asset container.");
        }
        return container;
    }
    /**
     * Releases the worker and rejects pending loads.
     */
    dispose() {
        this._terminateWorker();
        const error = new Error("USDFileLoader was disposed.");
        for (const controller of this._activeLoads) {
            controller.abort(error);
        }
        for (const request of this._pending.values()) {
            request.reject(error);
        }
        this._pending.clear();
    }
    async _loadAsync(scene, data, _rootUrl, onProgress, fileName, addToScene) {
        if (!(data instanceof ArrayBuffer) && !ArrayBuffer.isView(data)) {
            throw new Error("USDFileLoader expects binary USD data.");
        }
        const bytes = _toBytes(data);
        const files = _normalizeFiles(this._options.files);
        const requestId = this._nextRequestId++;
        const request = {
            type: "extract",
            requestId,
            asset: {
                bytes,
                files,
                fileName: _stagedFileName(this._options.rootFileName ?? fileName, bytes, this._options.rootFileName !== undefined),
                resolveByFileName: this._options.resolveByFileName ?? true,
                glueUrl: this._options.glueUrl,
                wasmUrl: this._options.wasmUrl,
                dataUrl: this._options.dataUrl,
            },
        };
        const transfer = [...new Set([bytes.buffer, ...Object.values(files ?? {}).map((file) => file.buffer)])];
        const controller = new AbortController();
        const signal = controller.signal;
        this._activeLoads.add(controller);
        try {
            const worker = this._getWorker(this._options.workerUrl);
            const response = await new Promise((resolve, reject) => {
                this._pending.set(requestId, {
                    resolve,
                    reject,
                    onProgress: (progress) => {
                        this._options.onProgress?.(progress);
                        onProgress?.({
                            lengthComputable: false,
                            loaded: _ProgressPhaseIndex[progress.phase],
                            total: 4,
                        });
                    },
                    onLog: this._options.onLog,
                });
                try {
                    worker.postMessage(request, transfer);
                }
                catch (error) {
                    this._pending.delete(requestId);
                    reject(error instanceof Error ? error : new Error(String(error)));
                }
            });
            const materializingProgress = {
                phase: "materializing",
                message: "Creating Babylon.js objects...",
            };
            this._options.onProgress?.(materializingProgress);
            onProgress?.({ lengthComputable: false, loaded: 4, total: 4 });
            signal.throwIfAborted();
            const materialized = await materializeCommandBuffers(scene, response.commands, response.data, addToScene, signal);
            const result = {
                container: materialized.container,
                timings: {
                    ...response.timings,
                    materializeMs: materialized.materializeMs,
                },
                statistics: response.statistics,
                missingAssets: response.missingAssets,
            };
            try {
                signal.throwIfAborted();
                this._options.onComplete?.({
                    timings: result.timings,
                    statistics: result.statistics,
                    missingAssets: result.missingAssets,
                });
                signal.throwIfAborted();
            }
            catch (error) {
                materialized.container.dispose();
                throw error;
            }
            return result;
        }
        finally {
            this._activeLoads.delete(controller);
            if (this._pending.size === 0) {
                this._terminateWorker();
            }
        }
    }
    _getWorker(workerUrl) {
        if (!workerUrl) {
            throw new Error("The USD worker URL was not configured.");
        }
        const currentLocation = globalThis.location;
        const resolvedUrl = new URL(String(workerUrl), currentLocation?.href ?? "http://localhost/").href;
        if (this._worker && this._workerUrl !== resolvedUrl) {
            if (this._pending.size > 0) {
                throw new Error("Cannot change the USD worker URL while requests are pending.");
            }
            this._terminateWorker();
        }
        if (this._worker) {
            return this._worker;
        }
        this._workerUrl = resolvedUrl;
        if (!currentLocation || new URL(resolvedUrl).origin === currentLocation.origin) {
            this._worker = new Worker(resolvedUrl, { type: "module" });
        }
        else {
            this._workerBlobUrl = URL.createObjectURL(new Blob([`import ${JSON.stringify(resolvedUrl)};`], { type: "application/javascript" }));
            this._worker = new Worker(this._workerBlobUrl, { type: "module" });
        }
        this._worker.addEventListener("message", (event) => {
            const response = event.data;
            const pending = this._pending.get(response.requestId);
            if (!pending) {
                return;
            }
            if (response.type === "progress") {
                pending.onProgress?.(response.progress);
                return;
            }
            if (response.type === "log") {
                pending.onLog?.(response.level >= 2 ? "error" : response.level === 1 ? "warning" : "info", response.message);
                return;
            }
            this._pending.delete(response.requestId);
            if (response.type === "error") {
                const error = new Error(response.message);
                error.stack = response.stack;
                pending.reject(error);
            }
            else {
                pending.resolve(response);
            }
        });
        const fail = (error) => {
            this._terminateWorker();
            for (const request of this._pending.values()) {
                request.reject(error);
            }
            this._pending.clear();
        };
        this._worker.addEventListener("error", (event) => {
            fail(event.error ?? new Error(event.message || `Could not load the USD worker from '${resolvedUrl}'.`));
        });
        this._worker.addEventListener("messageerror", () => {
            fail(new Error("The USD worker returned a message that could not be deserialized."));
        });
        return this._worker;
    }
    _terminateWorker() {
        this._worker?.terminate();
        this._worker = undefined;
        this._workerUrl = undefined;
        if (this._workerBlobUrl) {
            URL.revokeObjectURL(this._workerBlobUrl);
            this._workerBlobUrl = undefined;
        }
    }
}
/**
 * Default URLs for the prebuilt OpenUSD importer assets.
 */
USDFileLoader.DefaultConfiguration = {
    glueUrl: `${_DefaultAssetRootUrl}babylon-usd-importer.js`,
    wasmUrl: `${_DefaultAssetRootUrl}babylon-usd-importer.wasm`,
    dataUrl: `${_DefaultAssetRootUrl}babylon-usd-importer.data`,
    workerUrl: `${_DefaultAssetRootUrl}babylon-usd-importer.worker.js`,
};
const _ProgressPhaseIndex = {
    initializing: 1,
    staging: 2,
    extracting: 3,
    materializing: 4,
};
let _Registered = false;
/** @internal */
export function _RegisterUSDLoaderDependencies() {
    RegisterPbrMaterial();
    RegisterInstancedMesh();
    RegisterThinInstanceMesh();
}
/**
 * Registers the USD scene loader plugin and its Babylon.js runtime dependencies.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterUSDFileLoader() {
    if (_Registered) {
        return;
    }
    _Registered = true;
    _RegisterUSDLoaderDependencies();
    RegisterSceneLoaderPlugin(new USDFileLoader());
}
//# sourceMappingURL=usdFileLoader.pure.js.map